import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const ConfigPanel = ({ onConfigSave, config: initialConfig }) => {
  const [config, setConfig] = useState(initialConfig || {
    apiKey: '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',
    model: 'deepseek/deepseek-chat-v3-0324:free', // OpenRouter default model
    endpoint: 'openrouter', // Set OpenRouter as default provider
    ollamaEndpoint: 'https://llama.pressupwards.com/api/generate',
    useNoThink: false, // New property for the /nothink suffix option
    reoonApiKey: import.meta.env.VITE_REOON_API_KEY || 'smv1HVA2PV5X4me4m2e2dE0svhOn3WDO' // Reoon API key for email verification
  });

  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);
    }
  }, [initialConfig]);

  const handleSave = () => {
    // For OpenAI and OpenRouter, API key is required
    if ((config.endpoint === 'openai' || config.endpoint === 'openrouter') && !config.apiKey) {
      alert('Please provide an API key for ' + (config.endpoint === 'openai' ? 'OpenAI' : 'OpenRouter'));
      return;
    }

    // For Ollama, endpoint and model are required but API key is optional
    if (config.endpoint === 'ollama' && !config.ollamaEndpoint) {
      alert('Please provide an endpoint URL for Ollama');
      return;
    }

    if (!config.model) {
      alert('Please select or enter a model name');
      return;
    }

    onConfigSave(config);
  };

  const endpointOptions = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'openrouter', label: 'OpenRouter' },
    { value: 'ollama', label: 'Ollama' },
    { value: 'custom', label: 'Custom API' }
  ];

  // Suggested models based on selected endpoint
  const getSuggestedModels = () => {
    switch (config.endpoint) {
      case 'openai':
        return ['gpt-3.5-turbo', 'gpt-4o', 'gpt-4o-mini'];
      case 'openrouter':
        return ['deepseek/deepseek-chat-v3-0324:free', 'anthropic/claude-3-haiku', 'anthropic/claude-3-sonnet', 'google/gemini-pro'];
      case 'ollama':
        return ['llama3', 'llama3:8b', 'mistral', 'mixtral'];
      default:
        return ['gpt-3.5-turbo', 'llama3'];
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="config-card"
    >
      <div className="upload-header">
        <SafeIcon icon="FiSettings" />
        <h3 className="upload-title">API Configuration</h3>
      </div>
      <div className="config-form">
        <div className="form-group">
          <label className="form-label">API Provider</label>
          <select
            value={config.endpoint}
            onChange={(e) => setConfig({
              ...config,
              endpoint: e.target.value,
              // Reset model when changing provider to avoid incompatibility
              model: ''
            })}
            className="form-select"
          >
            {endpointOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {(config.endpoint === 'openai' || config.endpoint === 'openrouter' || config.endpoint === 'custom') && (
          <div className="form-group">
            <label className="form-label">API Key</label>
            <input
              type="password"
              value={config.apiKey}
              onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
              placeholder="Enter your API key"
              className="form-input"
            />
          </div>
        )}

        {config.endpoint === 'ollama' && (
          <div className="form-group">
            <label className="form-label">Ollama Endpoint</label>
            <input
              type="text"
              value={config.ollamaEndpoint}
              onChange={(e) => setConfig({ ...config, ollamaEndpoint: e.target.value })}
              placeholder="https://llama.pressupwards.com/api/generate"
              className="form-input"
            />
            <div className="input-help">
              Default: https://llama.pressupwards.com/api/generate
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Model</label>
          <div className="model-selection">
            <input
              type="text"
              value={config.model}
              onChange={(e) => setConfig({ ...config, model: e.target.value })}
              placeholder="Enter model name"
              className="form-input"
            />
            {getSuggestedModels().length > 0 && (
              <div className="model-suggestions">
                <div className="suggestion-label">Suggested models:</div>
                <div className="suggestion-chips">
                  {getSuggestedModels().map(model => (
                    <button
                      key={model}
                      className={`suggestion-chip ${config.model === model ? 'active' : ''}`}
                      onClick={() => setConfig({ ...config, model })}
                    >
                      {model}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="form-group">
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                className="checkbox-input"
                checked={config.useNoThink}
                onChange={(e) => setConfig({ ...config, useNoThink: e.target.checked })}
              />
              <span className="checkbox-text">Add "/nothink" suffix to prompts</span>
            </label>
            <div className="input-help">
              Adds the "/nothink" suffix to prompts and removes empty &lt;think&gt;&lt;/think&gt; tags from responses
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Reoon Email Verification API Key</label>
          <input
            type="password"
            value={config.reoonApiKey}
            onChange={(e) => setConfig({ ...config, reoonApiKey: e.target.value })}
            placeholder="Enter your Reoon API key"
            className="form-input"
          />
          <div className="input-help">
            API key for email verification service. Default key is provided.
          </div>
        </div>

        <button onClick={handleSave} className="upload-button">
          Save Configuration
        </button>
      </div>

      <style jsx="true">{`
        .checkbox-group {margin-top: 0.25rem;}
        .checkbox-label {display: flex;align-items: center;gap: 0.5rem;cursor: pointer;}
        .checkbox-input {width: 16px;height: 16px;accent-color: var(--primary-color);}
        .checkbox-text {font-size: 0.875rem;}
        .model-selection {display: flex;flex-direction: column;gap: 0.5rem;}
        .model-suggestions {margin-top: 0.25rem;}
        .suggestion-label {font-size: 0.75rem;color: var(--text-light);margin-bottom: 0.25rem;}
        .suggestion-chips {display: flex;flex-wrap: wrap;gap: 0.5rem;}
        .suggestion-chip {font-size: 0.75rem;padding: 0.25rem 0.5rem;border-radius: 4px;background-color: #f3f4f6;border: 1px solid #e5e7eb;cursor: pointer;transition: all 0.2s;}
        .suggestion-chip:hover {background-color: #e5e7eb;}
        .suggestion-chip.active {background-color: var(--primary-light);color: white;border-color: var(--primary-color);}
        .input-help {font-size: 0.75rem;color: var(--text-light);margin-top: 0.25rem;}
      `}</style>
    </motion.div>
  );
};

export default ConfigPanel;